import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import ErrorRoundedIcon from '@mui/icons-material/ErrorRounded';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import WifiTetheringRoundedIcon from '@mui/icons-material/WifiTetheringRounded';
import { Alert, Button, CircularProgress, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { getIntlLocale } from '@/shared/i18n';
import { FieldRow } from '@/features/store-settings';
import type { AiProviderCheckResult } from '@/features/store-settings';

export interface ProviderCheckPanelProps {
  probeSupported: boolean;
  disabled?: boolean;
  onCheck: () => void;
  isPending: boolean;
  result?: AiProviderCheckResult;
  requestError?: string;
  forbidden?: boolean;
  stale: boolean;
  usesUnsavedCredentials: boolean;
  onEnterKey?: () => void;
  onEditModel?: () => void;
  externalProviderPlatformAllowed: boolean;
  onFocusExternalSwitch?: () => void;
}

const OUTCOME_SEVERITY: Record<string, 'success' | 'warning' | 'error' | 'info'> = {
  OK: 'success',
  MODEL_UNVERIFIED: 'success',
  MODEL_NOT_FOUND: 'warning',
  CREDENTIAL_MISSING: 'warning',
  CREDENTIAL_REJECTED: 'error',
  PROVIDER_UNREACHABLE: 'error',
  TIMEOUT: 'error',
  PROVIDER_ERROR: 'error',
  PROVIDER_NOT_REGISTERED: 'error',
  EXTERNAL_PROVIDER_NOT_ALLOWED: 'warning',
  PROBE_NOT_SUPPORTED: 'info',
};

const OUTCOME_ICON: Record<
  'success' | 'warning' | 'error' | 'info',
  typeof CheckCircleRoundedIcon
> = {
  success: CheckCircleRoundedIcon,
  warning: WarningAmberRoundedIcon,
  error: ErrorRoundedIcon,
  info: InfoRoundedIcon,
};

/**
 * The bounded provider check (AI card doc §7.6) — a result panel, never a toast:
 * `success=false` is a result to render, not an error. Renders every outcome of the vocabulary,
 * falling back to the server's own `message` for one this build does not know.
 */
export function ProviderCheckPanel({
  probeSupported,
  disabled = false,
  onCheck,
  isPending,
  result,
  requestError,
  forbidden,
  stale,
  usesUnsavedCredentials,
  onEnterKey,
  onEditModel,
  externalProviderPlatformAllowed,
  onFocusExternalSwitch,
}: ProviderCheckPanelProps) {
  const { t } = useTranslation('chatbot');

  const knownOutcome = result ? OUTCOME_SEVERITY[result.outcome] : undefined;
  const severity = result ? (knownOutcome ?? 'error') : undefined;
  const Icon = severity ? OUTCOME_ICON[severity] : undefined;

  const sentence = result
    ? knownOutcome
      ? result.outcome === 'EXTERNAL_PROVIDER_NOT_ALLOWED'
        ? `${t('ai.check.outcomes.EXTERNAL_PROVIDER_NOT_ALLOWED')} ${
            externalProviderPlatformAllowed
              ? t('ai.check.externalFix')
              : t('ai.check.externalLocked')
          }`
        : t(`ai.check.outcomes.${result.outcome}`, { model: result.model })
      : result.message
    : undefined;

  return (
    <FieldRow label={t('ai.check.label')} description={t('ai.check.description')} alignTop>
      <Stack spacing={1.5} alignItems="flex-start">
        {usesUnsavedCredentials ? (
          <Typography variant="caption" color="text.secondary">
            {t('ai.check.usesSaved')}
          </Typography>
        ) : null}
        {!probeSupported ? (
          <Typography variant="caption" color="text.secondary">
            {t('ai.check.notSupported')}
          </Typography>
        ) : (
          <Button
            size="small"
            variant="outlined"
            onClick={onCheck}
            disabled={disabled || isPending}
            startIcon={
              isPending ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <WifiTetheringRoundedIcon />
              )
            }
          >
            {t('ai.check.button')}
          </Button>
        )}

        {requestError ? (
          <Alert
            severity="error"
            sx={{ width: '100%' }}
            action={
              <Button size="small" onClick={onCheck}>
                {t('dictionaries.retry')}
              </Button>
            }
          >
            {t('ai.check.requestError')} {requestError}
          </Alert>
        ) : forbidden ? (
          <Alert severity="error" sx={{ width: '100%' }}>
            {t('ai.check.forbidden')}
          </Alert>
        ) : result && severity && Icon ? (
          <Alert
            severity={severity}
            icon={<Icon fontSize="inherit" />}
            role="status"
            sx={{ width: '100%' }}
          >
            <Stack spacing={0.5}>
              <span>{sentence}</span>
              <Typography variant="caption" color="text.secondary">
                {t('ai.check.checkedAt', {
                  time: new Intl.DateTimeFormat(getIntlLocale(), {
                    dateStyle: 'medium',
                    timeStyle: 'short',
                  }).format(new Date(result.checkedAt)),
                })}
                {result.latencyMs != null
                  ? ` · ${t('ai.check.latency', { value: result.latencyMs })}`
                  : ''}
                {` · ${t('ai.check.checkedFor', { provider: t(`ai.providers.${result.provider}`, result.provider), model: result.model })}`}
              </Typography>
              {stale ? (
                <Typography variant="caption" color="warning.main">
                  {t('ai.check.stale')}
                </Typography>
              ) : null}
              {result.outcome === 'MODEL_NOT_FOUND' ||
              result.outcome === 'PROVIDER_NOT_REGISTERED' ? (
                <Button size="small" onClick={onEditModel} sx={{ alignSelf: 'flex-start' }}>
                  {t('ai.check.actions.editModel')}
                </Button>
              ) : null}
              {(result.outcome === 'CREDENTIAL_MISSING' ||
                result.outcome === 'CREDENTIAL_REJECTED') &&
              onEnterKey ? (
                <Button size="small" onClick={onEnterKey} sx={{ alignSelf: 'flex-start' }}>
                  {t('ai.check.actions.enterKey')}
                </Button>
              ) : null}
              {result.outcome === 'EXTERNAL_PROVIDER_NOT_ALLOWED' &&
              externalProviderPlatformAllowed &&
              onFocusExternalSwitch ? (
                <Button
                  size="small"
                  onClick={onFocusExternalSwitch}
                  sx={{ alignSelf: 'flex-start' }}
                >
                  {t('ai.external.label')}
                </Button>
              ) : null}
            </Stack>
          </Alert>
        ) : null}
      </Stack>
    </FieldRow>
  );
}
